import { Container } from 'aurelia-framework';
import { I18N } from 'aurelia-i18n';
import * as toastr from '~/vendor/toastr';

import { Key } from '~/utils/key';
import { KeyboardShortcut } from '~/utils/keyboard-shortcut';
import { StringUtils } from '~/utils/string-utils';

export class Notification {
	private static readonly INFO: string = 'notifications.info';
	private static readonly SUCCESS: string = 'notifications.success';
	private static readonly WARNING: string = 'notifications.warning';
	private static readonly ERROR: string = 'notifications.error';

	private static readonly MAX_TOASTS: number = 3;

	private static readonly commonToastOptions = {
		closeButton: true,
		extendedTimeOut: 30000,
		hideDuration: 300,
		hideMethod: 'slideUp',
		newestOnTop: false,
		onHidden: Notification.onHidden,
		onShown: Notification.onShown,
		positionClass: 'toast-bottom-left',
		preventDuplicates: false,
		progressBar: true,
		showDuration: 300,
		showMethod: 'slideDown',
		timeOut: 30000,
		toastClass: 'a-notification toastr',
	};

	private static readonly stickyOptions = {
		...Notification.commonToastOptions,
		extendedTimeOut: 0,
		timeOut: 0,
	};

	private static readonly closeOptions = {
		...Notification.commonToastOptions,
	};

	private static readonly successOptions = {
		...Notification.commonToastOptions,
		timeOut: 1432,
		extendedTimeOut: 1000,
		progressBar: false,
	};

	public static info(text: string) {
		const i18n = Container.instance.get(I18N);
		this.trimToasts();
		toastr.info(
			Notification.normalizeText(text),
			i18n.tr(Notification.INFO),
			this.closeOptions,
		);
	}

	public static success(text: string) {
		const i18n = Container.instance.get(I18N);
		this.trimToasts();
		toastr.success(
			Notification.normalizeText(text),
			i18n.tr(Notification.SUCCESS),
			this.successOptions,
		);
	}

	public static successWithTimeout(text: string, timeOut: number) {
		this.successOptions.timeOut = timeOut;
		this.success(text);
	}

	public static warning(text: string | Error) {
		const i18n = Container.instance.get(I18N);
		this.trimToasts();
		toastr.warning(
			Notification.normalizeText(text),
			i18n.tr(Notification.WARNING),
			this.closeOptions,
		);
	}

	public static error(text: string | Error) {
		const i18n = Container.instance.get(I18N);
		this.trimToasts();
		toastr.error(
			Notification.normalizeText(text),
			i18n.tr(this.ERROR),
			this.closeOptions,
		);
	}

	private static normalizeText(text: string | Error): string {
		// NOTE: Jake: 2019-10-22
		// Need to ensure this text is a string or else we might get an
		// error message like: "html.replace is not a function:"
		text = String(text);
		text = StringUtils.escapeHtml(text);
		// NOTE: Jae: 2020-02-04 - WEBUI-2975
		// Convert newlines to use <br/>
		text = text.replace(/\n/g, '<br/>');
		return text;
	}

	private static trimToasts() {
		$('#toast-container')
			.children('div.toast:last')
			.slice(this.MAX_TOASTS)
			.slideUp();
	}

	private static onShown(): void {
		const notifEl = this;
		if (!(notifEl instanceof HTMLElement)) {
			throw new Error('Expected "notifEl" to be a HTMLElement.');
		}
		KeyboardShortcut.subscribe(notifEl, Key.ESCAPE, () => {
			// NOTE(Jake): 2018-10-05 - WEBUI-727
			// Since we're manually removing the Toast notification, we
			// need to also call its cleanup method.
			Notification.onHidden(notifEl);

			// NOTE(Jake): 2018-10-05 - WEBUI-727
			// Intent here is to allow users to press ESCAPE to delete
			// the last created Toast notification.
			notifEl.remove();
		});
	}

	private static onHidden(el: Element): void {
		const notifEl = el;
		if (!(notifEl instanceof HTMLElement)) {
			throw new Error('Expected "notifEl" to be a HTMLElement.');
		}
		if (!notifEl.parentElement) {
			// NOTE(Jake): 2018-10-08
			// If no parent, then this element has already been removed, so
			// we should not unsubscribe it.
			return;
		}
		KeyboardShortcut.unsubscribeAll(notifEl);
	}
}
